import * as React from 'react';
import styled, { css } from 'styled-components';

import { sanitizeHtml } from '../utils/sanitizeHtml';

/**
 * Renders a rich-text message body inside a table cell.
 *
 * Message bodies are admin-authored HTML, so they always go through
 * `sanitizeHtml` (deny-by-default allowlist) before reaching
 * dangerouslySetInnerHTML - see utils/sanitizeHtml.test.ts.
 *
 * Block spacing is flattened so a multi-paragraph body does not blow up the
 * row height: paragraphs read as line breaks rather than spaced blocks.
 */
const RichMessageText = ({ html }: Props) => {
  const clean = React.useMemo(
    () => sanitizeHtml(html),
    [html],
  );

  if (!clean) return null;

  return (
    <Body
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

/**
 * Shared presentation for sanitised message HTML.
 *
 * Quill encodes font family and size as CLASSES (ql-font-serif, ql-size-large)
 * and quill.snow.css scopes those rules to `.ql-editor` - so outside the editor
 * they silently do nothing. The values below mirror Quill's own so a message
 * looks the same when read as it did when written. Colour and highlight need no
 * help: Quill writes those as inline style.
 */
export const richMessageStyles = css`
  p,
  blockquote,
  h1,
  h2,
  h3,
  h4 {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 18px;
  }

  a {
    color: var(--primary-700);
    text-decoration: underline;
  }

  blockquote {
    padding-left: 10px;
    border-left: 3px solid var(--border);
    color: var(--neutral-600);
  }

  img {
    max-width: 100%;
  }

  .ql-font-serif {
    font-family: Georgia, 'Times New Roman', serif;
  }
  .ql-font-monospace {
    font-family: Monaco, 'Courier New', monospace;
  }

  .ql-size-small {
    font-size: 0.75em;
  }
  .ql-size-large {
    font-size: 1.5em;
  }
  .ql-size-huge {
    font-size: 2.5em;
  }

  .ql-align-center {
    text-align: center;
  }
  .ql-align-right {
    text-align: right;
  }
  .ql-align-justify {
    text-align: justify;
  }
`;

const Body = styled.div`
  min-width: 0;
  text-align: left;
  overflow-wrap: anywhere;

  ${richMessageStyles}

  /* Only put air between blocks, never before the first or after the last. */
  > * + * {
    margin-top: 4px;
  }

  /* Headings must not dominate a table row. */
  h1,
  h2,
  h3,
  h4 {
    font-size: 1em;
    font-weight: 700;
    line-height: 1.35;
  }
`;

interface Props {
  html?: string;
}

export default RichMessageText;
